'use client'

import { useState } from 'react'
import { PropertyWithLandlord } from '@/types'
import { useCurrency } from '@/lib/currency'

interface AdminPropertyDetailsProps {
	property: PropertyWithLandlord
	onClose: () => void
	onVerify: (propertyId: string, verified: boolean) => Promise<void> | void
	onDelete: (propertyId: string) => Promise<void> | void
}

export default function AdminPropertyDetails({ property, onClose, onVerify, onDelete }: AdminPropertyDetailsProps) {
	const [activeImage, setActiveImage] = useState(0)
  const { format } = useCurrency()

	const formatPrice = (price: number) => format(price)

	const formatDuration = (months: number) => {
		if (months === 12) return '1 year'
		if (months === 6) return '6 months'
		if (months === 18) return '18 months'
		if (months === 24) return '2 years'
		return `${months} months`
	}

	const landlordName = () => {
		if (property.landlord?.kyc_data?.firstName && property.landlord?.kyc_data?.lastName) {
			return `${property.landlord.kyc_data.firstName} ${property.landlord.kyc_data.lastName}`
		}
		return property.landlord?.email || 'Landlord'
	}

	const contactHref = () => {
		const email = property.landlord?.email
		const subject = encodeURIComponent(`Admin inquiry about ${property.title}`)
		const body = encodeURIComponent(`Hello,\n\nThis is an administrative inquiry regarding your listing: ${property.title} (${property.location}).\n\n`)
		return email ? `mailto:${email}?subject=${subject}&body=${body}` : undefined
	}

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
			<div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
				<div className="p-6">
					<div className="flex items-start justify-between mb-4">
						<div>
							<h2 className="text-2xl font-bold text-gray-900">{property.title}</h2>
							<p className="text-gray-600">{property.location}</p>
						</div>
						<button onClick={onClose} className="text-gray-400 hover:text-gray-600">
							<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					</div>

					<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
						{/* Media */}
						<div className="lg:col-span-2">
							<div className="bg-gray-100 rounded-lg overflow-hidden">
								<div className="h-80 flex items-center justify-center">
									{property.images && property.images.length > 0 ? (
										<img
											src={typeof property.images[activeImage] === 'string' ? property.images[activeImage] : (property.images[activeImage] as any).url || property.images[activeImage]}
											alt={`${property.title} - image ${activeImage + 1}`}
											className="w-full h-full object-cover"
											onError={(e) => {
												;(e.currentTarget as HTMLImageElement).src = '/placeholder-property.svg'
											}}
										/>
									) : (
										<div className="text-gray-400 py-16">No images</div>
									)}
								</div>
								{property.images && property.images.length > 1 && (
									<div className="p-3 bg-white border-t">
										<div className="flex gap-2 overflow-x-auto">
											{property.images.map((img, idx) => {
												const imgUrl = typeof img === 'string' ? img : (img as any).url || img
												return (
													<button
														key={idx}
														onClick={() => setActiveImage(idx)}
														className={`w-20 h-16 rounded border ${idx === activeImage ? 'border-blue-600' : 'border-gray-200'}`}
														title={`View image ${idx + 1}`}
													>
														<img src={imgUrl} alt={`thumb ${idx + 1}`} className="w-full h-full object-cover" />
													</button>
												)
											})}
										</div>
									</div>
								)}
							</div>
							
							{/* Property Videos */}
							{property.videos && property.videos.length > 0 && (
								<div className="mt-4">
									<h4 className="font-medium text-gray-900 mb-3">Property Videos ({property.videos.length})</h4>
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										{property.videos.map((video, index) => (
											<div key={index} className="relative">
												<video
													src={video}
													className="w-full h-48 object-cover rounded-lg border"
													controls
													preload="metadata"
												/>
												<div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
													Video {index + 1}
												</div>
												{index === 0 && (
													<div className="absolute top-2 left-2 bg-purple-500 text-white text-xs px-2 py-1 rounded">
														Featured
													</div>
												)}
											</div>
										))}
									</div>
								</div>
							)}
							
							{/* House Documents */}
							{property.house_documents && property.house_documents.length > 0 && (
								<div className="mt-4">
									<h4 className="font-medium text-gray-900 mb-3">House Documents ({property.house_documents.length})</h4>
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										{property.house_documents.map((doc, index) => {
											const docUrl = typeof doc === 'string' ? doc : (doc as any).url || doc
											return (
												<div key={index} className="relative">
													<div className="w-full h-48 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
														<div className="text-center">
															<div className="text-4xl text-gray-400 mb-2">📄</div>
															<div className="text-sm text-gray-700">Document {index + 1}</div>
															<a
																href={docUrl}
																target="_blank"
																rel="noopener noreferrer"
																className="inline-block mt-2 px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
															>
																View Document
															</a>
														</div>
													</div>
												</div>
											)
										})}
									</div>
								</div>
							)}
						</div>

						{/* Details */}
						<div className="space-y-6">
							<div className="bg-white rounded-lg border p-4">
								<h3 className="font-medium text-gray-900 mb-2">Listing Details</h3>
								<div className="space-y-2 text-sm text-gray-700">
									<p><span className="text-gray-500">Price:</span> <span className="font-semibold text-green-600">{formatPrice(property.price)}</span> / month</p>
									<p><span className="text-gray-500">Duration:</span> <span className="font-semibold">{formatDuration(property.duration || 12)}</span></p>
									<p><span className="text-gray-500">Verification:</span> <span className={`font-semibold ${property.is_verified ? 'text-green-600' : 'text-yellow-700'}`}>{property.is_verified ? 'Verified' : 'Pending Review'}</span></p>
									<p><span className="text-gray-500">Photos:</span> <span className="font-semibold">{property.images?.length || 0}</span></p>
									<p><span className="text-gray-500">Videos:</span> <span className="font-semibold">{property.videos?.length || 0}</span></p>
									<p className="pt-2"><span className="text-gray-500">Description:</span></p>
									<p className="text-gray-800 leading-relaxed">{property.description}</p>
								</div>
							</div>

							{/* Attributes & Meta */}
							<div className="bg-white rounded-lg border p-4">
								<h3 className="font-medium text-gray-900 mb-2">Attributes & Meta</h3>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
									<div>
										<p><span className="text-gray-500">Property Type:</span> <span className="font-semibold">{property.property_type || '—'}</span></p>
										<p className="mt-1"><span className="text-gray-500">Views:</span> <span className="font-semibold">{typeof property.views === 'number' ? property.views : 0}</span></p>
										<p className="mt-1"><span className="text-gray-500">Created At:</span> <span className="font-semibold">{new Date(property.created_at).toLocaleString()}</span></p>
									</div>
									<div>
										<p className="text-gray-500 mb-1">Amenities:</p>
										{Array.isArray(property.amenities) && property.amenities.length > 0 ? (
											<div className="flex flex-wrap gap-2">
												{property.amenities.map((a, idx) => (
													<span key={`${a}-${idx}`} className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800 border">{a}</span>
												))}
											</div>
										) : (
											<p className="text-gray-600">No amenities listed</p>
										)}
									</div>
									<div className="md:col-span-2">
										<p className="text-gray-500 mb-1">Property ID:</p>
										<p className="font-mono text-xs break-all text-gray-700">{property.id}</p>
									</div>
								</div>
							</div>

							<div className="bg-white rounded-lg border p-4">
								<h3 className="font-medium text-gray-900 mb-2">Landlord</h3>
								<div className="space-y-1 text-sm text-gray-700">
									<p className="font-semibold">{landlordName()}</p>
									<p className="text-gray-600">{property.landlord?.email || 'No email on file'}</p>
									<div className="pt-2">
										{(() => {
											const href = contactHref()
											return (
												<button
													onClick={() => href && (window.location.href = href)}
													disabled={!href}
													title={!href ? 'Landlord email not available' : 'Email landlord'}
													className={`px-3 py-2 rounded-md text-sm ${href ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-300 text-gray-600 cursor-not-allowed'}`}
												>
													Email Landlord
												</button>
											)
										})()}
									</div>
								</div>
							</div>

							<div className="bg-white rounded-lg border p-4">
								<h3 className="font-medium text-gray-900 mb-3">Admin Actions</h3>
								<div className="flex flex-wrap gap-2">
									{property.is_verified === true ? (
										<button
											onClick={() => onVerify(property.id, false)}
											className="px-3 py-2 rounded-md text-sm bg-yellow-600 text-white hover:bg-yellow-700"
										>
											Unverify
										</button>
									) : (
										<button
											onClick={() => onVerify(property.id, true)}
											className="px-3 py-2 rounded-md text-sm bg-green-600 text-white hover:bg-green-700"
										>
											Verify
										</button>
									)}
									<button
										onClick={() => onDelete(property.id)}
										className="px-3 py-2 rounded-md text-sm bg-red-600 text-white hover:bg-red-700"
									>
										Delete
									</button>
									<button onClick={onClose} className="px-3 py-2 rounded-md text-sm bg-gray-100 text-gray-700 hover:bg-gray-200">
										Close
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}




